import React, { useState, useEffect } from 'react'
import QuickAssessmentStep from './QuickAssessmentStep'
import PackageSelectionStep from './PackageSelectionStep'
import DeceasedInformationStep from './DeceasedInformationStep'
import ProviderDashboardStep from './ProviderDashboardStep'
import { useNavigate } from 'react-router-dom'
import {
  QuickAssessmentResult,
  QuickAssessmentData,
  smartMatchingService,
} from '../../services/smart-matching.service'
import { Package } from '../../services/packages.service'
import { useCreateCaseWithProviders } from '../../hooks/useSmartMatching'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface FuneralWorkflowContainerProps {
  onComplete?: (caseData: any) => void
  initialData?: Partial<QuickAssessmentData>
  redirectTo?: string
}

type WorkflowStep = 'assessment' | 'package' | 'deceased' | 'dashboard'

interface WorkflowState {
  step: WorkflowStep
  assessmentResult: QuickAssessmentResult | null
  selectedPackage: Package | null
  caseData: any
  assessmentData: QuickAssessmentData | null
}

const FuneralWorkflowContainer: React.FC<FuneralWorkflowContainerProps> = ({
  onComplete,
  initialData,
  redirectTo = '/cases'
}) => {
  const navigate = useNavigate()
  const [isUpdatingCase, setIsUpdatingCase] = useState(false)
  const createCaseWithProvidersMutation = useCreateCaseWithProviders()
  const [workflowState, setWorkflowState] = useState<WorkflowState>({
    step: 'assessment',
    assessmentResult: null,
    selectedPackage: null,
    caseData: null,
    assessmentData: initialData || null,
  })

  const handleAssessmentComplete = (result: QuickAssessmentResult) => {
    setWorkflowState(prev => ({
      ...prev,
      step: 'package',
      assessmentResult: result,
      assessmentData: result.assessment,
    }))
  }

  const handlePackageSelect = (pkg: Package) => {
    setWorkflowState(prev => ({
      ...prev,
      step: 'deceased',
      selectedPackage: pkg,
    }))
  }

  const handleDeceasedComplete = (caseData: any) => {
    setWorkflowState(prev => ({
      ...prev,
      step: 'dashboard',
      caseData,
    }))
  }

  const handleDashboardComplete = async (selectedProviders: string[] = []) => {
    if (!workflowState.caseData || !workflowState.assessmentData || !workflowState.selectedPackage) {
      toast.error('Missing required data to create case')
      return
    }

    try {
      setIsUpdatingCase(true)

      // Get provider matching data from the ProviderDashboardStep
      const providerMatchingData = await smartMatchingService.findComprehensiveMatches({
        location: workflowState.assessmentData.currentLocation,
        urgencyLevel: workflowState.assessmentData.urgencyLevel,
        preferredBurialArea: workflowState.assessmentData.preferredBurialArea,
        selectedPackage: {
          id: workflowState.selectedPackage.id,
          name: workflowState.selectedPackage.name,
          price: workflowState.selectedPackage.price,
          includes: workflowState.selectedPackage.includes || [],
          excludes: workflowState.selectedPackage.excludes || []
        }
      })

      // Create case with providers (including the selected providers from user interaction)
      const updatedCase = await createCaseWithProvidersMutation.mutateAsync({
        deceasedData: workflowState.caseData,
        assessmentData: workflowState.assessmentData,
        selectedPackageId: workflowState.selectedPackage.id,
        customPackageConfig: null,
        providerMatchingData: providerMatchingData,
        selectedProviders: selectedProviders, // Use the providers selected by user
        providersSkipped: false // User is completing the process now
      })

      toast.success(`Case ${updatedCase.caseNumber} created successfully! ${selectedProviders.length > 0 ? `${selectedProviders.length} provider(s) booked.` : 'No providers selected.'}`)
      onComplete?.(updatedCase)
      navigate(redirectTo)
    } catch (error) {
      console.error('Failed to create case with providers:', error)
      toast.error('Failed to complete case setup. Please try again.')
    } finally {
      setIsUpdatingCase(false)
    }
  }

  const handleDashboardSkip = async () => {
    if (!workflowState.caseData || !workflowState.assessmentData || !workflowState.selectedPackage) {
      toast.error('Missing required data to create case')
      return
    }

    try {
      setIsUpdatingCase(true)

      // Get provider matching data from the ProviderDashboardStep
      const providerMatchingData = await smartMatchingService.findComprehensiveMatches({
        location: workflowState.assessmentData.currentLocation,
        urgencyLevel: workflowState.assessmentData.urgencyLevel,
        preferredBurialArea: workflowState.assessmentData.preferredBurialArea,
        selectedPackage: {
          id: workflowState.selectedPackage.id,
          name: workflowState.selectedPackage.name,
          price: workflowState.selectedPackage.price,
          includes: workflowState.selectedPackage.includes || [],
          excludes: workflowState.selectedPackage.excludes || []
        }
      })

      // Create case with providers and skip flag
      const updatedCase = await createCaseWithProvidersMutation.mutateAsync({
        deceasedData: workflowState.caseData,
        assessmentData: workflowState.assessmentData,
        selectedPackageId: workflowState.selectedPackage.id,
        customPackageConfig: null,
        providerMatchingData: providerMatchingData,
        selectedProviders: [], // No providers selected since we're skipping
        providersSkipped: true
      })

      toast.success(`Case ${updatedCase.caseNumber} created successfully! You can book providers later from your case dashboard.`)
      onComplete?.(updatedCase)
      navigate(redirectTo)
    } catch (error) {
      console.error('Failed to create case with providers:', error)
      toast.error('Failed to complete case setup. Please try again.')
    } finally {
      setIsUpdatingCase(false)
    }
  }

  const handleBack = () => {
    const stepOrder: WorkflowStep[] = ['assessment', 'package', 'deceased', 'dashboard']
    const currentIndex = stepOrder.indexOf(workflowState.step)
    if (currentIndex > 0) {
      setWorkflowState(prev => ({
        ...prev,
        step: stepOrder[currentIndex - 1],
      }))
    }
  }

  const currentStep = workflowState.step

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }, [currentStep])

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {currentStep === 'assessment' && (
          <QuickAssessmentStep
            onNext={handleAssessmentComplete}
            initialData={workflowState.assessmentData || undefined}
          />
        )}

        {currentStep === 'package' && workflowState.assessmentResult && (
          <PackageSelectionStep
            assessmentResult={workflowState.assessmentResult}
            onNext={handlePackageSelect}
            onBack={handleBack}
          />
        )}

        {currentStep === 'deceased' && workflowState.selectedPackage && workflowState.assessmentData && (
          <DeceasedInformationStep
            selectedPackage={workflowState.selectedPackage}
            assessmentData={workflowState.assessmentData}
            onNext={handleDeceasedComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === 'dashboard' && workflowState.caseData && workflowState.assessmentData && workflowState.selectedPackage && (
          <ProviderDashboardStep
            caseData={workflowState.caseData}
            assessmentData={workflowState.assessmentData}
            selectedPackage={workflowState.selectedPackage}
            onComplete={handleDashboardComplete}
            onSkip={handleDashboardSkip}
          />
        )}
      </div>
    </div>
  )
}

export default FuneralWorkflowContainer